import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Modal,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stack,
  Slider,
} from "@mui/material";
import {
  PlayArrow,
  Pause,
  Fullscreen,
  FullscreenExit,
} from "@mui/icons-material";
import { X } from "lucide-react";
import { useTheme } from "../../hooks/useTheme";
import Loader from "../Loader";

const HelpVideoModal = ({ open, onClose, videos = [], defaultIndex = 0 }) => {
  const [selectedIndex, setSelectedIndex] = useState(defaultIndex);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [videoAspect, setVideoAspect] = useState(16 / 9);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const { theme } = useTheme();

  const dark = theme === "dark";

  useEffect(() => {
    if (open) {
      setSelectedIndex(defaultIndex);
      setLoading(true);
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setVideoAspect(16 / 9);
    }
  }, [open, defaultIndex]);

  useEffect(() => {
    setLoading(true);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setVideoAspect(16 / 9);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [selectedIndex]);

  useEffect(() => {
    const handleFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleFsChange);
    return () => document.removeEventListener("fullscreenchange", handleFsChange);
  }, []);

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration || 0);
      const { videoWidth, videoHeight } = videoRef.current;
      if (videoWidth && videoHeight) setVideoAspect(videoWidth / videoHeight);
    }
  };

  const handlePlayPause = () => {
    if (!videoRef.current) return;
    if (isPlaying) videoRef.current.pause();
    else videoRef.current.play();
  };

  const handleSeek = (_, val) => {
    if (videoRef.current) {
      videoRef.current.currentTime = val;
      setCurrentTime(val);
    }
  };

  const handleFullscreen = async () => {
    if (!document.fullscreenElement) await containerRef.current?.requestFullscreen();
    else await document.exitFullscreen();
  };

  const formatTime = (s) => {
    if (!s || !isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const currentVideo = videos[selectedIndex];
  const controlColor = dark ? "white" : "#525252";

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="help-video-modal">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "80%",
          maxWidth: 1100,
          maxHeight: "90vh",
          bgcolor: dark ? "#111827" : "#ffffff",
          boxShadow: 24,
          borderRadius: 2,
          overflow: "hidden",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* ── Left: sidebar ── */}
        <Box
          sx={{
            width: { xs: "100%", md: 260 },
            bgcolor: dark ? "#000000" : "#FAFAFA",
            overflowY: "auto",
            borderRight: `1px solid ${dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.08)"}`,
            flexShrink: 0,
          }}
        >
          <Box sx={{ p: 1.5, borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}` }}>
            <Typography variant="h6" sx={{ color: dark ? "white" : "#525252", fontSize: "1rem" }}>
              Tutorial Videos
            </Typography>
            <Typography variant="body2" sx={{ color: dark ? "rgba(255,255,255,0.7)" : "#888", mt: 0.5, fontSize: "0.75rem" }}>
              Click on a video to watch it
            </Typography>
          </Box>

          <List disablePadding dense>
            {videos.map((video, index) => {
              const isActive = index === selectedIndex;
              return (
                <React.Fragment key={index}>
                  <ListItem
                    sx={{
                      cursor: "pointer",
                      bgcolor: isActive ? "rgba(79,70,229,0.1)" : "transparent",
                      borderLeft: isActive ? "4px solid #4f46e5" : "4px solid transparent",
                      py: 1,
                      "&:hover": { bgcolor: "rgba(79,70,229,0.08)" },
                    }}
                    onClick={() => setSelectedIndex(index)}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: 22, height: 22, minWidth: 22,
                              borderRadius: "50%",
                              bgcolor: isActive ? "#4f46e5" : dark ? "rgba(255,255,255,0.1)" : "#525252",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              color: "white", fontWeight: "bold", fontSize: "0.7rem", flexShrink: 0,
                            }}
                          >
                            {index + 1}
                          </Box>
                          <Typography
                            sx={{
                              fontWeight: isActive ? "bold" : "normal",
                              color: isActive ? "#4f46e5" : dark ? "white" : "#525252",
                              fontSize: "0.85rem",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {video.title}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  <Divider sx={{ bgcolor: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)" }} />
                </React.Fragment>
              );
            })}
          </List>
        </Box>

        {/* ── Right: video player ── */}
        <Box
          ref={containerRef}
          sx={{
            flex: "1 1 0",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            bgcolor: dark ? "#000000" : "#FAFAFA",
          }}
        >
          {/* Video area — sized by the video's real aspect ratio */}
          <Box
            sx={{
              position: "relative",
              width: "100%",
              aspectRatio: String(videoAspect),
            }}
          >
            {/* Close button */}
            <Box sx={{ position: "absolute", top: 10, right: 10, zIndex: 4 }}>
              <IconButton onClick={onClose} sx={{ color: "white" }}>
                <X size={20} />
              </IconButton>
            </Box>

            {/* Loader */}
            {loading && (
              <Box sx={{ position: "absolute", inset: 0, zIndex: 5, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: dark ? "#000000" : "#FAFAFA" }}>
                <Loader />
              </Box>
            )}

            {currentVideo?.url ? (
              <>
                <video
                  ref={videoRef}
                  key={currentVideo.url}
                  src={currentVideo.url}
                  style={{ width: "100%", height: "100%", display: "block" }}
                  onLoadedData={() => setLoading(false)}
                  onLoadedMetadata={handleLoadedMetadata}
                  onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime || 0)}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onEnded={() => setIsPlaying(false)}
                />

                {/* Centre play button */}
                {!loading && !isPlaying && (
                  <Box
                    onClick={handlePlayPause}
                    sx={{
                      position: "absolute", inset: 0, zIndex: 2,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: "pointer", bgcolor: "rgba(0,0,0,0.25)",
                    }}
                  >
                    <Box
                      sx={{
                        width: 64, height: 64, borderRadius: "50%",
                        bgcolor: "rgba(255,255,255,0.92)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
                        transition: "transform 0.2s",
                        "&:hover": { transform: "scale(1.1)" },
                      }}
                    >
                      <PlayArrow sx={{ color: "#1a1a1a", fontSize: 38, ml: 0.5 }} />
                    </Box>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography sx={{ color: dark ? "rgba(255,255,255,0.4)" : "#aaa", fontSize: "0.9rem" }}>
                  No video available
                </Typography>
              </Box>
            )}
          </Box>

          {/* ── Controls bar ── */}
          {!loading && currentVideo?.url && (
            <Box
              sx={{
                px: 2, py: 1.5,
                bgcolor: dark ? "#000000" : "#FAFAFA",
                borderTop: `1px solid ${dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.08)"}`,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Slider
                value={currentTime}
                min={0}
                max={duration || 0}
                step={0.1}
                onChange={handleSeek}
                size="small"
                sx={{
                  p: 0,
                  "& .MuiSlider-rail": {
                    bgcolor: dark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)",
                    height: 4,
                  },
                  "& .MuiSlider-track": {
                    background: "linear-gradient(to right, #ec4899, #3b82f6)",
                    border: "none",
                    height: 4,
                  },
                  "& .MuiSlider-thumb": {
                    width: 12,
                    height: 12,
                    bgcolor: "#3b82f6",
                    "&:hover, &.Mui-focusVisible": { boxShadow: "0 0 0 8px rgba(59,130,246,0.16)" },
                    "&:before": { display: "none" },
                  },
                }}
              />

              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <IconButton onClick={handlePlayPause} size="small" sx={{ color: controlColor }}>
                    {isPlaying ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
                  </IconButton>
                  <Typography variant="body2" sx={{ color: controlColor, fontSize: "0.8rem", ml: 0.5 }}>
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </Typography>
                </Stack>
                <IconButton onClick={handleFullscreen} size="small" sx={{ color: controlColor }}>
                  {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
                </IconButton>
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default HelpVideoModal;
